'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import { useSiteLanguage } from '@/components/common/SiteLanguageProvider';

export default function FAQSection() {
  const [isHydrated, setIsHydrated] = useState(false);
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);
  const { language } = useSiteLanguage();

  const copy = {
    en: {
      title: 'Frequently Asked Questions',
      description: 'Everything you need to know about working with us',
      promptTitle: 'Still have questions?',
      promptDescription: "We're here to help. Reach out and we'll get back to you within 24 hours.",
      contactCta: 'Contact Us',
      faqs: [
        {
          id: 1,
          question: 'How long does it take to build a product?',
          answer:
            "Timeline varies based on complexity, but most MVPs are delivered in 6-12 weeks. We work in 2-week sprints with regular check-ins, so you'll see progress continuously.",
        },
        {
          id: 2,
          question: 'What is your pricing model?',
          answer:
            'We offer flexible engagement models: fixed-price projects for well-defined scopes, time and materials for evolving requirements, and retainers for ongoing partnerships.',
        },
        {
          id: 3,
          question: 'Do you provide ongoing support after launch?',
          answer:
            'Absolutely. We offer maintenance and support packages tailored to your needs, including bug fixes, monitoring, security updates, and feature enhancements.',
        },
        {
          id: 4,
          question: 'Can you work with our existing team?',
          answer:
            'Yes. We often collaborate with in-house teams, either augmenting capabilities or taking ownership of specific product areas.',
        },
      ],
    },
    fr: {
      title: 'Questions fréquentes',
      description: 'Tout ce que vous devez savoir pour travailler avec nous',
      promptTitle: 'Vous avez encore des questions ?',
      promptDescription: 'Nous sommes là pour vous aider. Écrivez-nous et nous reviendrons vers vous sous 24 heures.',
      contactCta: 'Nous contacter',
      faqs: [
        {
          id: 1,
          question: 'Combien de temps faut-il pour construire un produit ?',
          answer:
            'Le délai dépend de la complexité, mais la plupart des MVP sont livrés en 6 à 12 semaines. Nous travaillons en sprints de deux semaines avec des points réguliers.',
        },
        {
          id: 2,
          question: 'Quel est votre modèle de tarification ?',
          answer:
            'Nous proposons des modes de collaboration flexibles : forfait pour les périmètres clairs, temps et matériel pour les besoins évolutifs, et retainer pour les partenariats continus.',
        },
        {
          id: 3,
          question: 'Fournissez-vous un support après le lancement ?',
          answer:
            'Oui. Nous proposons des offres de maintenance et de support adaptées, avec corrections, supervision, mises à jour de sécurité et évolutions produit.',
        },
        {
          id: 4,
          question: 'Pouvez-vous travailler avec notre équipe existante ?',
          answer:
            'Oui. Nous collaborons souvent avec des équipes internes, soit en renfort, soit en prenant en charge certaines parties du produit.',
        },
      ],
    },
  } as const;

  const t = copy[language];

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const toggleFAQ = (id: number) => {
    if (!isHydrated) return;
    setOpenFAQ(openFAQ === id ? null : id);
  };

  return (
    <section id="faq" className="py-24 bg-card">
      <div className="max-w-4xl mx-auto px-6 lg:px-12">
        <div className="text-center mb-16">
          <h2 className="font-display text-section-h2 mb-4 text-gradient-neon">{t.title}</h2>
          <p className="font-sans text-supporting-lg font-medium text-muted-foreground">
            {t.description}
          </p>
        </div>

        <div className="space-y-4">
          {t.faqs.map((faq) => (
            <div
              key={faq.id}
              className="bg-background border border-border rounded-lg overflow-hidden hover:border-primary transition-colors duration-300"
            >
              <button
                onClick={() => toggleFAQ(faq.id)}
                className="w-full p-6 text-left flex items-center justify-between gap-4 hover:bg-muted/50 transition-colors duration-200"
              >
                <h3 className="font-sans text-card-title text-foreground pr-4">{faq.question}</h3>
                <Icon
                  name="ChevronDownIcon"
                  size={24}
                  className={`text-primary flex-shrink-0 transition-transform duration-300 ${
                    openFAQ === faq.id ? 'rotate-180' : ''
                  }`}
                />
              </button>

              {isHydrated && openFAQ === faq.id && (
                <div className="px-6 pb-6 border-t border-border">
                  <p className="font-sans text-body text-muted-foreground mt-4">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-12 bg-background border border-primary/20 rounded-lg p-8 text-center">
          <h3 className="font-sans text-card-title text-foreground mb-3">{t.promptTitle}</h3>
          <p className="font-sans text-body text-muted-foreground mb-6">{t.promptDescription}</p>
          <Link
            href="/contact"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground text-nav font-medium rounded-sm hover:shadow-neon-cyan transition-all duration-300 hover:scale-105"
          >
            <Icon name="ChatBubbleLeftRightIcon" size={20} />
            {t.contactCta}
          </Link>
        </div>
      </div>
    </section>
  );
}
